import React from 'react';
import {Person} from '../../types/person.ts';
import {openModal, refreshPersons} from '../../hooks/useInit.ts';
import {ModalMode, PersonModal} from '../modals/PersonModal';
import PersonModalDelete from '../modals/PersonModalDelete/PersonModalDelete';
import {
  Label,
  LabelType,
  ModalSize,
  ParagraphSize,
  RowAction,
  TableColumn
} from '@fs/uikit-react';
import styles from './table.module.css';

// Returns black or white depending on which contrasts better with the given background color
function getContrastColor(bgColor: string): string {
  // Normalize hex like #RGB or #RRGGBB
  const hex = bgColor.trim();
  let r = 0, g = 0, b = 0;

  const parseHex = (h: string) => {
    const clean = h.replace('#', '');
    if (clean.length === 3) {
      r = parseInt(clean[0] + clean[0], 16);
      g = parseInt(clean[1] + clean[1], 16);
      b = parseInt(clean[2] + clean[2], 16);
      return true;
    }
    if (clean.length === 6) {
      r = parseInt(clean.slice(0, 2), 16);
      g = parseInt(clean.slice(2, 4), 16);
      b = parseInt(clean.slice(4, 6), 16);
      return true;
    }
    return false;
  };

  if (hex.startsWith('#')) {
    parseHex(hex);
  } else if (hex.startsWith('rgb')) {
    // rgb(a) format
    const nums = hex
      .replace(/rgba?\(/, '')
      .replace(/\)/, '')
      .split(',')
      .map(v => parseFloat(v.trim()));
    r = nums[0] ?? 0;
    g = nums[1] ?? 0;
    b = nums[2] ?? 0;
  } else {
    // Fallback to gray if unknown format
    r = g = b = 204;
  }

  // Calculate relative luminance
  const srgb = [r, g, b].map(v => v / 255).map(v => (
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  ));
  const L = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];

  // Return black for light backgrounds, white for dark
  return L > 0.179 ? '#000000' : '#ffffff';
}

export const getTableColumns = (): TableColumn[] => {
  return [
    {
      key: 'lastName',
      title: 'Прізвище',
      width: '20%',
      sortable: true,
    },
    {
      key: 'firstName',
      title: 'Ім\'я',
      width: '20%',
      sortable: true,
    },
    {
      key: 'categories',
      title: 'Категорії',
      width: '40%',
      render: (_text: string, record: Person) => {
        const categories = record.categories || [];
        return (
          <div className={styles.categoriesCell}>
            {categories.map((c, idx) => {
              const color = c.color || '#cccccc';
              const textColor = getContrastColor(color);
              const key = c.id || `${c.name}-${idx}`;
              return (
                <span
                  key={key}
                  className={styles.categoryPill}
                  style={{
                    // CSS variables consumed by the class styles
                    ['--category-bg' as never]: color,
                    ['--category-fg' as never]: textColor
                  }}
                >
                  <Label
                    text={c.name}
                    paragraphSize={ParagraphSize.SIZE_12}
                    type={LabelType.PARAGRAPH}
                    className={styles.label}
                  />
                </span>
              );
            })}
          </div>
        );
      }
    },
    {
      key: 'comment',
      title: 'Коментар',
      width: '20%',
    }
  ];
};

export const getRowActions = (handleDeletePerson: (record: Person) => void): RowAction[] => {
  return [
    {
      type: 'delete',
      onClick: (record: Person) => {
        handleDeletePerson(record);
      }
    }
  ];
};

export const filteredPersons = (persons: Person[], searchValue: string): Person[] => {
  if (searchValue === '') return persons || [];

  const searchLower = searchValue.toLowerCase();
  return (persons || []).filter(person => (
    person.lastName?.toLowerCase().includes(searchLower) ||
    person.firstName?.toLowerCase().includes(searchLower) ||
    person.fathersName?.toLowerCase().includes(searchLower) ||
    (person.identificationNumber?.toLowerCase().includes(searchLower)) ||
    (person.occupation?.toLowerCase().includes(searchLower)) ||
    (person.comment?.toLowerCase().includes(searchLower)) ||
    ((person.categories || []).some(c => c.name?.toLowerCase().includes(searchLower)))
  ));
};

export const handleCreatePerson = () => {
  openModal(
    ModalSize.SMALL,
    'Створити особу',
    <PersonModal mode={ModalMode.CREATE} onSuccess={refreshPersons} />
  );
};

export const handleEditPerson = (person: Person) => {
  openModal(
    ModalSize.SMALL,
    'Редагувати особу',
    <PersonModal mode={ModalMode.EDIT} person={person} onSuccess={refreshPersons} />
  );
};

export const handleDeletePerson = (person: Person) => {
  openModal(
    ModalSize.SMALL,
    'Видалити особу',
    <PersonModalDelete person={person} onSuccess={refreshPersons} />
  );
};
